import React, { useState, useEffect } from 'react';
import { Clock, CheckCircle, XCircle } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { getDealerships, updateDealershipStatus } from '../../services/api';
import { toast } from 'sonner';

const DealershipsAdmin = () => {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');

  const fetchData = async () => {
    try {
      const res = await getDealerships({ status: filter || undefined });
      setApps(res.data.applications);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [filter]);

  const handleStatusChange = async (id, newStatus) => {
    try {
      await updateDealershipStatus(id, newStatus);
      toast.success('Status updated');
      fetchData();
    } catch { toast.error('Update failed'); }
  };

  const statusColor = (s) => {
    switch(s) {
      case 'pending': return 'bg-yellow-50 text-yellow-700';
      case 'approved': return 'bg-green-50 text-green-700';
      case 'rejected': return 'bg-red-50 text-red-700';
      default: return 'bg-gray-50 text-gray-700';
    }
  };

  return (
    <div className="space-y-6" data-testid="dealerships-admin">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dealership Applications</h1>
        <p className="text-sm text-gray-500">{apps.length} applications</p>
      </div>

      <div className="flex gap-2">
        {['', 'pending', 'approved', 'rejected'].map((s) => (
          <Button key={s} size="sm" variant={filter === s ? 'default' : 'outline'} onClick={() => setFilter(s)} className={filter === s ? 'bg-[#2E3A6F]' : ''}>
            {s || 'All'}
          </Button>
        ))}
      </div>

      {loading ? (
        <div className="space-y-3">{[...Array(3)].map((_, i) => <Card key={i} className="animate-pulse"><CardContent className="p-4 h-24" /></Card>)}</div>
      ) : apps.length === 0 ? (
        <Card className="border-dashed"><CardContent className="p-12 text-center text-gray-500">No applications found</CardContent></Card>
      ) : (
        <div className="space-y-3">
          {apps.map((app) => (
            <Card key={app.id} className="hover:shadow-sm transition-shadow" data-testid={`dealership-${app.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="font-semibold text-gray-900">{app.name}</h3>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium capitalize ${statusColor(app.status)}`}>{app.status}</span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs text-gray-600">
                      <p><strong>Company:</strong> {app.company_name}</p>
                      <p><strong>Email:</strong> {app.email}</p>
                      <p><strong>Phone:</strong> {app.phone}</p>
                      <p><strong>GST:</strong> {app.gst_number}</p>
                      <p><strong>City:</strong> {app.city}, {app.state}</p>
                      <p><strong>Type:</strong> {app.business_type}</p>
                    </div>
                    {app.message && <p className="text-sm text-gray-700 mt-2 bg-gray-50 p-3 rounded-lg">{app.message}</p>}
                    <p className="text-[10px] text-gray-400 mt-2">{app.created_at && new Date(app.created_at).toLocaleString()}</p>
                  </div>
                  <div className="flex flex-col gap-1 ml-3">
                    {app.status !== 'approved' && (
                      <Button size="sm" variant="outline" onClick={() => handleStatusChange(app.id, 'approved')} className="text-green-600 text-xs">
                        <CheckCircle size={12} className="mr-1" />Approve
                      </Button>
                    )}
                    {app.status !== 'rejected' && (
                      <Button size="sm" variant="outline" onClick={() => handleStatusChange(app.id, 'rejected')} className="text-red-600 text-xs">
                        <XCircle size={12} className="mr-1" />Reject
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default DealershipsAdmin;
